import React, { useState, useRef } from "react";
import toast from "react-hot-toast";
import API from "../utils/api";

/* ─── simple CSV parser ─────────────────────────────────────────────────── */
function parseCSV(text) {
  const lines = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
  const nonEmpty = lines.filter(l => l.trim() !== "");
  if (nonEmpty.length < 2) return { headers: [], rows: [] };

  const splitRow = (line) => {
    const result = [];
    let cur = "";
    let inQuote = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { inQuote = !inQuote; }
      else if (ch === "," && !inQuote) { result.push(cur.trim()); cur = ""; }
      else { cur += ch; }
    }
    result.push(cur.trim());
    return result;
  };

  const headers = splitRow(nonEmpty[0]).map(h => h.replace(/^"|"$/g, ""));
  const rows = nonEmpty.slice(1).map(line => {
    const vals = splitRow(line).map(v => v.replace(/^"|"$/g, ""));
    const obj = {};
    headers.forEach((h, i) => { obj[h] = vals[i] !== undefined ? vals[i] : ""; });
    return obj;
  });
  return { headers, rows };
}

/* ─── CSV template definitions ──────────────────────────────────────────── */
const EXISTING_HEADERS = [
  "employee_name", "institution_name", "institution_type", "city",
  "contact_name", "contact_phone", "contact_email",
  "lead_status", "lead_stage", "pipeline_stage",
  "last_visit_date", "student_count", "value_per_student", "notes"
];

const NEW_SCHOOL_HEADERS = [
  "employee_name", "institution_name", "institution_type", "city",
  "contact_name", "contact_phone", "lead_status", "notes"
];

const EXISTING_SAMPLE = [
  ["Ravi Kumar",   "St. Joseph's College",   "college",  "Chennai",  "Dr. Anand",  "9876543210", "anand@stjosephs.edu",  "hot",  "prospect",  "visited",     "2025-03-12", "1200", "3500", "Strong interest, follow up this month"],
  ["Priya Mehta",  "Delhi Public School",    "school",   "Mumbai",   "Mrs. Sharma","9123456789", "sharma@dps.in",       "warm", "qualified", "follow_up",   "2025-02-28", "800",  "2800", "Needs principal approval"],
];

const NEW_SCHOOL_SAMPLE = [
  ["Ravi Kumar",   "Sunrise Academy",  "school",   "Coimbatore", "Mr. Nair",    "9988776655", "warm",  "Contacted via reference, good potential"],
  ["Priya Mehta",  "Global Institute", "college",  "Pune",       "Dr. Kapoor",  "9911223344", "cold",  "Cold outreach, needs nurturing"],
];

function makeCSV(headers, rows) {
  const escape = (v) => (String(v).includes(",") ? `"${v}"` : v);
  return [headers, ...rows].map(r => r.map(escape).join(",")).join("\n");
}

function downloadCSV(filename, content) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/* ─── status badge colours ──────────────────────────────────────────────── */
function statusBadge(s) {
  if (s === "ok") return { bg: "#f0fdf4", color: "#16a34a", label: "✓ OK" };
  return { bg: "#fef2f2", color: "#dc2626", label: "✗ Error" };
}

/* ─── UploadCard ────────────────────────────────────────────────────────── */
function UploadCard({ accent, title, icon, description, templateHeaders, templateRows, templateFilename, onUpload }) {
  const [file, setFile]         = useState(null);
  const [headers, setHeaders]   = useState([]);
  const [rows, setRows]         = useState([]);
  const [preview, setPreview]   = useState([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult]     = useState(null);
  const inputRef                = useRef();

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!f.name.endsWith(".csv")) { toast.error("Please select a .csv file"); return; }
    setFile(f);
    setResult(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const { headers: h, rows: r } = parseCSV(ev.target.result);
      setHeaders(h);
      setRows(r);
      setPreview(r.slice(0, 5));
    };
    reader.readAsText(f);
  };

  const handleUpload = async () => {
    if (!rows.length) return toast.error("No data rows to upload");
    setUploading(true);
    try {
      const res = await onUpload(rows);
      setResult(res);
      toast.success(`Done — ${res.updated_leads} institutions processed`);
    } catch (err) {
      toast.error(err.response?.data?.error || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const reset = () => {
    setFile(null); setHeaders([]); setRows([]); setPreview([]); setResult(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const colW = { flex: "0 0 160px", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

  return (
    <div style={{ background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: 18, padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
        <div style={{ width: 48, height: 48, borderRadius: 14, background: accent + "18", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0 }}>{icon}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: "#1e293b" }}>{title}</div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 3, lineHeight: 1.5 }}>{description}</div>
        </div>
      </div>

      {/* Template download */}
      <div style={{ background: "#f8fafc", borderRadius: 12, padding: "10px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>CSV Template</div>
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>Download, fill in your data, then upload below</div>
        </div>
        <button
          onClick={() => downloadCSV(templateFilename, makeCSV(templateHeaders, templateRows))}
          style={{ background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 8, padding: "7px 14px", fontSize: 12, fontWeight: 700, color: "#475569", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}>
          ⬇ Download Template
        </button>
      </div>

      {/* File picker */}
      {!file ? (
        <div
          onClick={() => inputRef.current?.click()}
          style={{ border: "2px dashed #cbd5e1", borderRadius: 14, padding: "28px 20px", textAlign: "center", cursor: "pointer", transition: "border-color 0.2s" }}
          onMouseEnter={e => e.currentTarget.style.borderColor = accent}
          onMouseLeave={e => e.currentTarget.style.borderColor = "#cbd5e1"}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📂</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#475569" }}>Click to select your CSV file</div>
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>Only .csv format supported</div>
          <input ref={inputRef} type="file" accept=".csv" onChange={handleFile} style={{ display: "none" }} />
        </div>
      ) : (
        <div>
          {/* File info bar */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10, padding: "10px 14px", marginBottom: 12 }}>
            <span style={{ fontSize: 18 }}>📄</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#15803d" }}>{file.name}</div>
              <div style={{ fontSize: 11, color: "#16a34a" }}>{rows.length} rows detected</div>
            </div>
            <button onClick={reset} style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#94a3b8", padding: 4 }}>✕</button>
          </div>

          {/* Preview table */}
          {preview.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
                Preview (first {preview.length} of {rows.length} rows)
              </div>
              <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: 10 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                  <thead>
                    <tr style={{ background: "#f8fafc" }}>
                      {headers.map(h => (
                        <th key={h} style={{ padding: "7px 10px", textAlign: "left", fontWeight: 700, color: "#64748b", whiteSpace: "nowrap", borderBottom: "1px solid #e2e8f0" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((row, i) => (
                      <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                        {headers.map(h => (
                          <td key={h} style={{ padding: "7px 10px", color: "#475569", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row[h] || ""}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {rows.length > 5 && (
                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 5 }}>+ {rows.length - 5} more rows</div>
              )}
            </div>
          )}

          {/* Upload button */}
          {!result && (
            <button
              onClick={handleUpload}
              disabled={uploading}
              style={{ width: "100%", background: uploading ? "#94a3b8" : accent, color: "#fff", border: "none", borderRadius: 10, padding: "12px", fontSize: 13, fontWeight: 800, cursor: uploading ? "not-allowed" : "pointer" }}>
              {uploading ? "⏳ Uploading..." : `⬆ Upload ${rows.length} Rows`}
            </button>
          )}
        </div>
      )}

      {/* Result summary */}
      {result && (
        <div style={{ background: "#f8fafc", borderRadius: 12, padding: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#1e293b", marginBottom: 12 }}>Upload Complete</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8, marginBottom: 14 }}>
            {[
              { label: "Processed",  value: result.updated_leads, color: accent },
              { label: "Visits Added", value: result.created_visits, color: "#16a34a" },
              { label: "Errors",     value: result.errors, color: result.errors > 0 ? "#dc2626" : "#94a3b8" },
            ].map(s => (
              <div key={s.label} style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 8px", textAlign: "center" }}>
                <div style={{ fontSize: 22, fontWeight: 900, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Row-level results */}
          {result.results && result.results.length > 0 && (
            <div style={{ maxHeight: 220, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
              {result.results.map((r, i) => {
                const b = statusBadge(r.status);
                return (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: b.bg, borderRadius: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: b.color, flexShrink: 0 }}>{b.label}</span>
                    <span style={{ fontSize: 11, color: "#475569", flex: 1 }}>
                      Row {r.row}{r.employee ? ` — ${r.employee}` : ""}{r.institution ? ` → ${r.institution}` : ""}
                      {r.message ? `: ${r.message}` : ""}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          <button
            onClick={reset}
            style={{ marginTop: 12, width: "100%", background: "#f1f5f9", border: "none", borderRadius: 10, padding: "10px", fontSize: 12, fontWeight: 700, color: "#475569", cursor: "pointer" }}>
            Upload Another File
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── Main BulkUpload component ─────────────────────────────────────────── */
export default function BulkUpload({ accent }) {
  const handleUpload = async (rows) => {
    const res = await API.post("/bulk-ops/institutions", { rows });
    return res.data;
  };

  return (
    <div>
      {/* Page header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 18, fontWeight: 900, color: "#1e293b", marginBottom: 4 }}>📥 Data Upload</div>
        <div style={{ fontSize: 13, color: "#64748b" }}>
          Import institution data in bulk via CSV. Choose the right upload type below.
        </div>
      </div>

      {/* How it works info box */}
      <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 14, padding: "14px 16px", marginBottom: 20, display: "flex", gap: 12 }}>
        <span style={{ fontSize: 20, flexShrink: 0 }}>ℹ️</span>
        <div style={{ fontSize: 12, color: "#1e40af", lineHeight: 1.7 }}>
          <strong>How it works:</strong> Each row must have an <strong>employee_name</strong> (matching a name in the system) and an <strong>institution_name</strong>.
          If the institution already exists for that employee, it will be <strong>updated</strong>. If it's new, it will be <strong>created</strong>.
          A visit record is also created whenever a <strong>last_visit_date</strong> is provided.
        </div>
      </div>

      {/* Two upload cards */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <UploadCard
          accent={accent}
          title="Upload Existing Institutions"
          icon="🏫"
          description="Import your existing institution database with visit history, contact details, lead status, and revenue data. Use this to bring in historical records and assign them to your team."
          templateHeaders={EXISTING_HEADERS}
          templateRows={EXISTING_SAMPLE}
          templateFilename="existing_institutions_template.csv"
          onUpload={handleUpload}
        />
        <UploadCard
          accent={accent}
          title="Upload New Schools"
          icon="🆕"
          description="Add new target schools or colleges and assign them to employees. Use this for fresh prospecting — no visit history needed. Just institution name, city, and employee assignment."
          templateHeaders={NEW_SCHOOL_HEADERS}
          templateRows={NEW_SCHOOL_SAMPLE}
          templateFilename="new_schools_template.csv"
          onUpload={handleUpload}
        />
      </div>

      {/* Column reference guide */}
      <div style={{ marginTop: 24, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 16, overflow: "hidden" }}>
        <div style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9", fontSize: 13, fontWeight: 800, color: "#1e293b" }}>
          📋 CSV Column Reference
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0 }}>
          {[
            { col: "employee_name", req: true,  note: "Must match exactly an employee name in the system" },
            { col: "institution_name", req: true,  note: "School / college / coaching centre name" },
            { col: "institution_type", req: false, note: "school / college / coaching / other" },
            { col: "city", req: false, note: "City or location of institution" },
            { col: "contact_name", req: false, note: "Name of point of contact at institution" },
            { col: "contact_phone", req: false, note: "Phone number of contact" },
            { col: "contact_email", req: false, note: "Email of contact" },
            { col: "lead_status", req: false, note: "hot / warm / cold (defaults to warm)" },
            { col: "lead_stage", req: false, note: "prospect / qualified / demo / proposal / negotiation / won / lost" },
            { col: "pipeline_stage", req: false, note: "visited / follow_up / demo / proposal / negotiation / won" },
            { col: "last_visit_date", req: false, note: "YYYY-MM-DD format — creates a visit record" },
            { col: "student_count", req: false, note: "Number of students at institution" },
            { col: "value_per_student", req: false, note: "Program value per student (₹)" },
            { col: "notes", req: false, note: "Any additional notes or comments" },
          ].map((item, i) => (
            <div key={item.col} style={{ padding: "10px 20px", borderBottom: "1px solid #f1f5f9", borderRight: i % 2 === 0 ? "1px solid #f1f5f9" : "none", display: "flex", gap: 10, alignItems: "flex-start" }}>
              <div style={{ flexShrink: 0, marginTop: 2 }}>
                <span style={{ background: item.req ? "#fef2f2" : "#f8fafc", color: item.req ? "#dc2626" : "#94a3b8", borderRadius: 100, padding: "1px 6px", fontSize: 10, fontWeight: 700 }}>
                  {item.req ? "REQ" : "OPT"}
                </span>
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#1e293b", fontFamily: "monospace" }}>{item.col}</div>
                <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>{item.note}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
